import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useSimulation } from './context/SimulationContext';
import { useSimulationSocket } from './hooks/useSimulationSocket';
import { useSessionSetup } from './hooks/useSessionSetup';
import { useReconnection } from './hooks/useReconnection';
import { useProctoring } from './hooks/useProctoring';
import WaitingScreen from './components/WaitingScreen/WaitingScreen';
import SimulationShell from './components/SimulationShell/SimulationShell';
import SessionEndScreen from './components/SessionEndScreen/SessionEndScreen';
import PausedScreen from './components/PausedScreen/PausedScreen';
import InstructionsScreen from './components/InstructionsScreen/InstructionsScreen';
import InterviewService from '../../Services/interview.service';

function SimulationRuntime() {
  const [searchParams] = useSearchParams();
  const interviewSessionId = searchParams.get('sessionId');

  const {
    sessionStatus,
    sessionEnd,
    simSessionId,
    error,
    dispatch,
    socketRef,
  } = useSimulation();

  const { createSession, resumeSession } = useSessionSetup(interviewSessionId);
  const [initialized, setInitialized] = useState(false);
  const [instructionsAcknowledged, setInstructionsAcknowledged] = useState(false);

  // Connect WebSocket when session is active
  useSimulationSocket({
    simSessionId,
    interviewSessionId,
    sessionStatus,
    socketRef,
    dispatch,
  });

  useReconnection();
  useProctoring(sessionStatus === 'active');

  // Initialize session on mount
  useEffect(() => {
    if (!interviewSessionId || initialized) return;
    setInitialized(true);

    const init = async () => {
      try {
        const interviewService = new InterviewService();
        const session =
          await interviewService.findSessionById(interviewSessionId);
        console.log("Interview session details:", session);

        if (!session) {
          dispatch({
            type: 'SET_ERROR',
            payload: 'Interview session not found',
          });
          dispatch({ type: 'SET_STATUS', payload: 'failed' });
          return;
        }

        // Check if there's already a sim session for this interview session
        // by looking at session data or trying to create one
        const roundId = session.interviewRoundId;
        if (!roundId) {
          dispatch({
            type: 'SET_ERROR',
            payload: 'No round ID found for this session',
          });
          dispatch({ type: 'SET_STATUS', payload: 'failed' });
          return;
        }

        await createSession(interviewSessionId, roundId);
      } catch (err: any) {
        // If session already exists (409 or similar), try to resume
        if (err?.response?.status === 409 && err?.response?.data?.sessionId) {
          await resumeSession(err.response.data.sessionId);
        } else {
          dispatch({
            type: 'SET_ERROR',
            payload:
              err?.response?.data?.message ?? 'Failed to initialize simulation',
          });
          dispatch({ type: 'SET_STATUS', payload: 'failed' });
        }
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewSessionId]);

  // Show instructions before starting
  if (!instructionsAcknowledged) {
    return (
      <InstructionsScreen onStart={() => setInstructionsAcknowledged(true)} />
    );
  }

  // Error state
  if (error || sessionStatus === 'failed') {
    return (
      <Result
        status="error"
        title="Simulation Error"
        subTitle={error ?? 'Something went wrong. Please try again.'}
        extra={
          <Button
            type="primary"
            onClick={() => {
              dispatch({ type: 'RESET' });
              setInitialized(false);
            }}
          >
            Retry
          </Button>
        }
      />
    );
  }

  // Loading / pending / scenarioReady
  if (
    sessionStatus === 'loading' ||
    sessionStatus === 'pending' ||
    sessionStatus === 'scenarioReady'
  ) {
    return <WaitingScreen />;
  }

  // Session ended
  if (sessionEnd || sessionStatus === 'completed') {
    return <SessionEndScreen />;
  }

  // Session paused — show resume/restart options
  if (sessionStatus === 'paused') {
    return (
      <PausedScreen
        onRestart={() => {
          dispatch({ type: 'RESET' });
          setInitialized(false);
        }}
      />
    );
  }

  // Active simulation
  if (sessionStatus === 'active') {
    return <SimulationShell />;
  }

  return <WaitingScreen />;
}

export default SimulationRuntime;
